import { useAuthStore } from "@/features/auth/store/auth-store";

/**
 * Base URL for the Django REST API. Only `BUN_PUBLIC_*` env vars are exposed
 * to the client bundle (see bunfig.toml). Falls back to the local dev API,
 * whose CORS allow-list already includes the Bun dev server (localhost:3000).
 */
const API_BASE_URL = process.env.BUN_PUBLIC_API_URL ?? "http://localhost:8000/api/v1";

/** Thrown for any non-2xx response so callers can branch on status/detail. */
export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON-serializable request body; sets Content-Type automatically. */
  json?: unknown;
  /** Multipart request body (file uploads). Content-Type is left unset so the
   * browser adds the multipart boundary. Mutually exclusive with `json`. */
  form?: FormData;
  /** Attach the Bearer access token (default true). Set false for login/refresh. */
  auth?: boolean;
}

function pickErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const record = data as Record<string, unknown>;
  // DRF's generic errors use { detail: "..." }.
  if (typeof record.detail === "string") return record.detail;
  // Serializer field errors use { field: ["msg", ...] } (or a bare string) —
  // surface the first one rather than a generic fallback, since server-side
  // validation is the real trust boundary.
  for (const value of Object.values(record)) {
    if (typeof value === "string") return value;
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  }
  return fallback;
}

/** Refresh in-flight guard so parallel 401s trigger a single refresh call. */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens, clearSession } = useAuthStore.getState();
  // Any path that can't produce a fresh token must also clear the session, so
  // a dead token never leaves `isAuthenticated: true` (which would strand the
  // user on a private page silently failing every request).
  if (!refreshToken) {
    clearSession();
    return null;
  }

  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!res.ok) {
      clearSession();
      return null;
    }

    // Enveloped by the backend's APIJSONRenderer: { success, message, data }.
    const { data } = (await res.json()) as { data: { access: string; refresh: string } };
    setTokens(data.access, data.refresh);
    return data.access;
  } catch {
    // network failure — treat as an invalid session locally
    clearSession();
    return null;
  }
}

async function requestOnce(path: string, options: ApiFetchOptions): Promise<Response> {
  const { json, form, auth = true, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);

  if (json !== undefined) finalHeaders.set("Content-Type", "application/json");

  if (auth) {
    const token = useAuthStore.getState().accessToken;
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  return fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : form,
  });
}

/**
 * Thin fetch wrapper: prefixes the API base URL, attaches the Bearer token,
 * serializes JSON, and transparently refreshes an expired access token once
 * on a 401 before retrying. Throws `ApiError` on non-2xx.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  let res = await requestOnce(path, options);

  if (res.status === 401 && (options.auth ?? true)) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    const newToken = await refreshPromise;
    if (newToken) res = await requestOnce(path, options);
  }

  if (res.status === 204 || res.status === 205) return undefined as T;

  const contentType = res.headers.get("Content-Type") ?? "";
  const data = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, pickErrorMessage(data, `Request failed (${res.status})`), data);
  }

  return data as T;
}

/**
 * Like `apiFetch`, but returns the raw `Response` (after the Bearer-token attach
 * and single-retry-on-401-refresh) so callers can inspect status/headers and
 * read a `Blob` — for binary downloads and endpoints whose body varies by status
 * (e.g. a bulk download that streams a zip on 200 but returns JSON on a 202
 * "queued" response). Throws `ApiError` on non-2xx.
 */
export async function apiFetchResponse(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  let res = await requestOnce(path, options);

  if (res.status === 401 && (options.auth ?? true)) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    const newToken = await refreshPromise;
    if (newToken) res = await requestOnce(path, options);
  }

  if (!res.ok) {
    throw new ApiError(res.status, `Request failed (${res.status})`, null);
  }

  return res;
}

/**
 * Like `apiFetch`, but returns the raw response body as a `Blob` — for binary
 * downloads (e.g. generated PDFs) that shouldn't be JSON-parsed. Shares the same
 * Bearer-token attach and single-retry-on-401-refresh behavior.
 */
export async function apiFetchBlob(path: string, options: ApiFetchOptions = {}): Promise<Blob> {
  const res = await apiFetchResponse(path, options);
  return res.blob();
}
